<template>
  <div
    class="modal fade"
    id="deployHistoryModal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="deployHistoryModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="deployHistoryModalLabel">
            <i class="fas fa-history mr-2"></i>
            Histórico do pipeline
            <span v-if="targetLabel"> — {{ targetLabel }}</span>
          </h5>
          <button type="button" class="close" data-dismiss="modal">
            <span>&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center text-muted py-3">
            <i class="fas fa-circle-notch fa-spin mr-1"></i>
            Carregando...
          </div>

          <div v-else-if="runs.length === 0" class="text-center text-muted py-3">
            Nenhuma execução encontrada.
          </div>

          <table v-else class="history-table">
            <thead>
              <tr>
                <th class="graph-col"></th>
                <th>Quando</th>
                <th>Status</th>
                <th>Commit</th>
                <th>Release (pasta)</th>
                <th>Disparado por</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="run in runs" :key="run.id">
                <td class="graph-col">
                  <span class="commit-graph-line"></span>
                  <span class="commit-graph-dot"></span>
                </td>
                <td>{{ run.created_at_f }}</td>
                <td>
                  <span class="history-status" :class="'status-' + run.status">
                    {{ statusLabel(run.status) }}
                  </span>
                </td>
                <td>
                  <div v-if="run.commit_sha" class="commit-info">
                    <code class="commit-sha">{{ run.commit_sha }}</code>
                    <span class="commit-message" :title="run.commit_message">{{ run.commit_message }}</span>
                    <span class="commit-author">{{ run.commit_author }}</span>
                  </div>
                  <span v-else class="text-muted">-</span>

                  <svg
                    v-if="run.commits && run.commits.length > 1 && hasBranching(run.commits)"
                    class="commit-branch-graph"
                    :width="graphWidth(run.commits)"
                    :height="graphHeight(run.commits)"
                  >
                    <path
                      v-for="(edge, idx) in buildGraph(run.commits).edges"
                      :key="'e' + idx"
                      :d="edgePath(edge, run.commits.length)"
                      class="graph-edge"
                      :class="{ 'graph-edge-branch': edge.lane1 !== edge.lane2 }"
                    />
                    <circle
                      v-for="node in buildGraph(run.commits).nodes"
                      :key="'n' + node.index"
                      :cx="nodeX(node.index, run.commits.length)"
                      :cy="nodeY(node.lane)"
                      :r="node.index === 0 || node.isMerge ? 4 : 1.75"
                      class="graph-node"
                      :class="{ 'graph-node-head': node.index === 0, 'graph-node-merge': node.isMerge }"
                    >
                      <title>{{ node.sha }} — {{ node.message }} ({{ node.author }})</title>
                    </circle>
                  </svg>
                </td>
                <td>
                  <code v-if="run.release_folder">{{ run.release_folder }}</code>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>{{ run.user ? run.user.name : "-" }}</td>
                <td class="text-center">
                  <button
                    v-if="run.release_folder"
                    class="btn-rollback"
                    :disabled="rollingBackId === run.id"
                    @click="rollback(run)"
                  >
                    <i
                      class="fas"
                      :class="rollingBackId === run.id ? 'fa-circle-notch fa-spin' : 'fa-undo'"
                    ></i>
                    {{ rollingBackId === run.id ? "Revertendo..." : "Reverter pra essa" }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && totalCount > perPage" class="history-paginator">
            <small class="text-muted">
              Página {{ page }} de {{ lastPage }} — {{ totalCount }} execução(ões)
            </small>
            <div class="history-paginator-actions">
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="page <= 1 || loading"
                @click="fetchPage(page - 1)"
              >
                ‹ Anterior
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="page >= lastPage || loading"
                @click="fetchPage(page + 1)"
              >
                Próxima ›
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DeployHistoryModal",

  data() {
    return {
      runs: [],
      targetLabel: "",
      targetKey: "",
      filterParams: {},
      rollingBackId: null,
      loading: false,
      page: 1,
      perPage: 10,
      totalCount: 0,
      lastPage: 1,
      graphXSpacing: 16,
      graphYSpacing: 12,
      graphPadding: 8,
    };
  },

  methods: {
    /**
     * Só vale desenhar o mini-grafo se houver de fato um merge (2+ pais)
     * em algum commit do intervalo — sem isso, seria só uma linha reta
     * sem informação nenhuma além do que o timeline já mostra.
     */
    hasBranching(commits) {
      return commits.some((commit) => {
        const parents = commit.parents_array
          || (commit.parents ? commit.parents.split(" ").filter(Boolean) : []);
        return parents.length > 1;
      });
    },
    /**
     * Monta as raias (lanes) do mini-grafo de ramificações a partir dos
     * commits de um deploy (mais novo primeiro). Cada commit entra na raia
     * que alguém mais novo já esperava (pelo sha do pai); se nenhuma raia
     * espera por ele, abre uma nova — é aí que aparece um "fork" visual.
     * Merge commits (2+ pais) abrem uma raia extra pro pai secundário.
     */
    buildGraph(commits) {
      const lanes = [];
      const nodes = [];
      const edges = [];

      commits.forEach((commit, index) => {
        const parents = commit.parents_array
          || (commit.parents ? commit.parents.split(" ").filter(Boolean) : []);

        let laneIdx = lanes.findIndex((l) => l && l.expectedSha === commit.sha);
        if (laneIdx === -1) {
          laneIdx = lanes.length;
          lanes.push(null);
        } else {
          const pending = lanes[laneIdx];
          edges.push({ x1: pending.fromIndex, lane1: pending.fromLane, x2: index, lane2: laneIdx });
        }

        nodes.push({
          index,
          lane: laneIdx,
          sha: commit.sha,
          message: commit.message,
          author: commit.author,
          isMerge: parents.length > 1,
        });

        if (parents.length === 0) {
          lanes[laneIdx] = null;
        } else {
          lanes[laneIdx] = { expectedSha: parents[0], fromIndex: index, fromLane: laneIdx };
          for (let p = 1; p < parents.length; p++) {
            lanes.push({ expectedSha: parents[p], fromIndex: index, fromLane: laneIdx });
          }
        }
      });

      return { nodes, edges, laneCount: Math.max(lanes.length, 1) };
    },
    graphWidth(commits) {
      return (commits.length - 1) * this.graphXSpacing + this.graphPadding * 2;
    },
    graphHeight(commits) {
      const { laneCount } = this.buildGraph(commits);
      return (laneCount - 1) * this.graphYSpacing + this.graphPadding * 2;
    },
    nodeX(index, total) {
      return this.graphPadding + (total - 1 - index) * this.graphXSpacing;
    },
    nodeY(lane) {
      return this.graphPadding + lane * this.graphYSpacing;
    },
    edgePath(edge, total) {
      const x1 = this.nodeX(edge.x1, total);
      const y1 = this.nodeY(edge.lane1);
      const x2 = this.nodeX(edge.x2, total);
      const y2 = this.nodeY(edge.lane2);
      if (edge.lane1 === edge.lane2) {
        return `M ${x1} ${y1} L ${x2} ${y2}`;
      }
      const midX = (x1 + x2) / 2;
      return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
    },
    open(filterParams, targetKey, targetLabel) {
      this.filterParams = filterParams || {};
      this.targetKey = targetKey || "";
      this.targetLabel = targetLabel || "";
      $("#deployHistoryModal").modal("show");
      this.fetchPage(1);
    },

    async fetchPage(page) {
      this.loading = true;
      this.page = page;
      try {
        const api = this.$store.state.api + "deploy-runs/history";
        const params = { ...this.filterParams, target: this.targetKey, page, limit: this.perPage };
        const res = await axios.get(api, { params });
        this.runs = res.data.data || [];
        this.totalCount = res.data.total || 0;
        this.lastPage = res.data.last_page || 1;
      } catch (e) {
        this.$message(
          "Ops",
          "Não foi possível carregar o histórico do pipeline.",
          "error"
        );
        this.runs = [];
      } finally {
        this.loading = false;
      }
    },

    statusLabel(status) {
      const labels = {
        triggered: "Rodando...",
        running: "Rodando...",
        success: "Sucesso",
        failed: "Falhou",
      };
      return labels[status] || status;
    },

    async rollback(run) {
      this.rollingBackId = run.id;
      try {
        const api = this.$store.state.api + `deploy-runs/${run.id}/rollback`;
        const res = await axios.post(api);
        this.$message(
          "Sucesso",
          res.data.message || "Rollback disparado com sucesso.",
          "success"
        );
        this.fetchPage(1);
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          "Não foi possível disparar o rollback.";
        this.$message("Ops", msg, "error");
      } finally {
        this.rollingBackId = null;
      }
    },
  },
};
</script>

<style scoped>
.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table thead th {
  text-align: left;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  border-bottom: 2px solid #ebe9f1;
  padding: 8px 10px;
}

.history-table tbody td {
  padding: 10px;
  border-bottom: 1px solid #ebe9f1;
  vertical-align: middle;
  font-size: 0.85rem;
}

.graph-col {
  width: 26px;
  padding: 0 !important;
  position: relative;
}

.commit-graph-line {
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #7367f0;
}

.history-table tbody tr:first-child .commit-graph-line {
  top: 50%;
}

.history-table tbody tr:last-child .commit-graph-line {
  bottom: 50%;
}

.commit-graph-dot {
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #7367f0;
  border: 2px solid #fff;
  z-index: 1;
}

.commit-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 220px;
}

.commit-sha {
  font-size: 0.7rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  padding: 1px 5px;
  border-radius: 4px;
  width: fit-content;
}

.commit-message {
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.commit-author {
  font-size: 0.7rem;
  color: #6e6b7b;
}

.commit-branch-graph {
  display: block;
  margin-top: 4px;
}

.graph-edge {
  fill: none;
  stroke: #d8d6de;
  stroke-width: 1.5;
}

.graph-edge-branch {
  stroke: #ff9f43;
  stroke-width: 2;
}

.graph-node {
  fill: #d8d6de;
  stroke: none;
  cursor: default;
}

.graph-node-head {
  fill: #7367f0;
}

.graph-node-merge {
  fill: #ff9f43;
}

body.dark-layout .graph-edge {
  stroke: #4a5063;
}

body.dark-layout .graph-node {
  fill: #4a5063;
}

body.dark-layout .graph-node-head {
  fill: #7367f0;
}

body.dark-layout .graph-node-merge {
  fill: #ff9f43;
}

.history-status {
  font-weight: 600;
  font-size: 0.78rem;
}

.history-status.status-success {
  color: #24a148;
}

.history-status.status-failed {
  color: #da1e28;
}

.history-status.status-triggered,
.history-status.status-running {
  color: #0f61f9;
}

.btn-rollback {
  border: none;
  background: #d9822b;
  color: #fff;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.btn-rollback i {
  font-size: 0.9rem;
}

.btn-rollback:hover:not(:disabled) {
  background: #b8691a;
}

.btn-rollback:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

body.dark-layout .btn-rollback {
  background: #d9822b;
  color: #fff;
}

body.dark-layout .btn-rollback:hover:not(:disabled) {
  background: #eb972f;
}

body.dark-layout .commit-graph-dot {
  border-color: #283046;
}

body.dark-layout .commit-author {
  color: #b4b7bd;
}

.history-paginator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  flex-wrap: wrap;
  gap: 8px;
}

.history-paginator-actions {
  display: flex;
  gap: 8px;
}
</style>
